// /api/posting-alerts — drives the Home PostingAlertsTile.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "./api";

export interface PostingAlert {
  id: string;
  company_id: string;
  job_id: string;
  title: string;
  url: string;
  location: string | null;
  run_id: string | null;
  created_at: number;
  emailed_at: number | null;
  seen_at: number | null;
  company_name: string | null;
}

export interface PostingAlertsResponse {
  alerts: PostingAlert[];
  total_unseen: number;
}

export function usePostingAlerts() {
  return useQuery({
    queryKey: ["posting-alerts"],
    queryFn: () => api<PostingAlertsResponse>("/api/posting-alerts"),
    // Alerts land on the daily cron tick; a minute of staleness is fine and
    // keeps Home navigations cheap.
    staleTime: 60 * 1000,
  });
}

// Optimistic removal so a dismissed row vanishes without the round-trip;
// rollback on error, settle with an invalidate (same shape as
// useDismissWeeklySummary).
export function useMarkAlertSeen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api<{ ok: boolean }>(`/api/posting-alerts/${id}/seen`, { method: "POST" }),
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: ["posting-alerts"] });
      const prev = qc.getQueryData<PostingAlertsResponse>(["posting-alerts"]);
      if (prev) {
        qc.setQueryData<PostingAlertsResponse>(["posting-alerts"], {
          alerts: prev.alerts.filter((a) => a.id !== id),
          total_unseen: Math.max(0, prev.total_unseen - 1),
        });
      }
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) qc.setQueryData(["posting-alerts"], ctx.prev);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ["posting-alerts"] });
    },
  });
}

export function useMarkAllAlertsSeen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api<{ ok: boolean; dismissed: number }>("/api/posting-alerts/seen-all", {
        method: "POST",
      }),
    onMutate: async () => {
      await qc.cancelQueries({ queryKey: ["posting-alerts"] });
      const prev = qc.getQueryData<PostingAlertsResponse>(["posting-alerts"]);
      qc.setQueryData<PostingAlertsResponse>(["posting-alerts"], {
        alerts: [],
        total_unseen: 0,
      });
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) qc.setQueryData(["posting-alerts"], ctx.prev);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ["posting-alerts"] });
    },
  });
}
